import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface Segment<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
  // A count that is somebody's job reads in the attention colour, as the rail's pending count does.
  urgent?: boolean;
}

/**
 * One choice out of a few, side by side: a status filter, file-or-paste. Pressed segments lift onto the
 * surface rather than filling with the accent, so a row of them never competes with the page's one
 * primary button.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Segment<T>[];
  label: string;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("bg-bank-surface-3 inline-flex flex-wrap gap-1 rounded-lg p-[3px]", className)}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-md px-3 text-[13px] transition-colors",
              "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:outline-none",
              on ? "bg-bank-surface font-semibold shadow-xs" : "hover:bg-bank-surface-2",
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span
                className={cn(
                  "font-mono text-[11.5px] tabular-nums",
                  o.urgent && o.count > 0 ? "text-bank-attention-ink" : "text-bank-muted",
                )}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
